import type { FilterService } from '../shared/filter.service';
import type { SearchService } from '../shared/search.service';
import type { UiStateService } from '../shared/ui-state.service';
import type { ScrollPositionService } from '../shared/scroll-position.service';

export const TRANSLOADING_LIST_SNAPSHOT_KEY = 'dgag_transloading_list_snapshot';
export const TRANSLOADING_EXPECT_RESTORE_KEY = 'dgag_transloading_expect_restore';

export interface TransloadingListSnapshot {
  tab: 'pending' | 'outgoing' | 'completed';
  filters: Record<string, string[]>;
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  passportId: string;
  scrollY?: number;
}

export interface LeaveTransloadingPersonaListStateDeps {
  filterService: FilterService;
  searchService: SearchService;
  ui: UiStateService;
  scrollPosition: ScrollPositionService;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isFiltersRecord(value: unknown): value is Record<string, string[]> {
  if (!isRecord(value)) {
    return false;
  }
  for (const v of Object.values(value)) {
    if (!isStringArray(v)) {
      return false;
    }
  }
  return true;
}

function isTransloadingListSnapshot(value: unknown): value is TransloadingListSnapshot {
  if (!isRecord(value)) {
    return false;
  }
  const tab = value['tab'];
  if (tab !== 'pending' && tab !== 'outgoing' && tab !== 'completed') {
    return false;
  }
  if (!isFiltersRecord(value['filters'])) {
    return false;
  }
  if (typeof value['searchQuery'] !== 'string') {
    return false;
  }
  if (typeof value['currentPage'] !== 'number' || !Number.isFinite(value['currentPage'])) {
    return false;
  }
  if (typeof value['itemsPerPage'] !== 'number' || !Number.isFinite(value['itemsPerPage'])) {
    return false;
  }
  if (typeof value['sortColumn'] !== 'string') {
    return false;
  }
  const dir = value['sortDirection'];
  if (dir !== 'asc' && dir !== 'desc') {
    return false;
  }
  if (typeof value['passportId'] !== 'string') {
    return false;
  }
  const scrollY = value['scrollY'];
  if (scrollY !== undefined && (typeof scrollY !== 'number' || !Number.isFinite(scrollY))) {
    return false;
  }
  return true;
}

export function peekTransloadingExpectRestore(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(TRANSLOADING_EXPECT_RESTORE_KEY) === '1';
  } catch {
    return false;
  }
}

export function hasTransloadingListSnapshot(): boolean {
  try {
    const raw = globalThis.sessionStorage?.getItem(TRANSLOADING_LIST_SNAPSHOT_KEY);
    return typeof raw === 'string' && raw.length > 0;
  } catch {
    return false;
  }
}

export function clearTransloadingListRestoreState(): void {
  try {
    globalThis.sessionStorage?.removeItem(TRANSLOADING_LIST_SNAPSHOT_KEY);
    globalThis.sessionStorage?.removeItem(TRANSLOADING_EXPECT_RESTORE_KEY);
  } catch {
    // sessionStorage may be unavailable (SSR / private mode)
  }
}

export function saveTransloadingListSnapshot(snapshot: TransloadingListSnapshot): void {
  try {
    globalThis.sessionStorage?.setItem(TRANSLOADING_LIST_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export function parseTransloadingListSnapshot(): TransloadingListSnapshot | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(TRANSLOADING_LIST_SNAPSHOT_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isTransloadingListSnapshot(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function leaveTransloadingPersonaListState(deps: LeaveTransloadingPersonaListStateDeps): void {
  clearTransloadingListRestoreState();
  deps.filterService.clear();
  deps.searchService.clear();
  deps.ui.setSearchOpen(false);
  deps.ui.setFiltersOpen(false);
  deps.ui.setStatusSelected('pending');
  deps.scrollPosition.clearScrollPosition('/transloading');
}
